import { apiService } from "./api";
import { API_CONFIG } from "../config/api";
import {
    Product,
    Category,
    CategoryWithChildren,
    CategoryBreadcrumb,
    Brand,
    Stock,
    StockMovement,
    CreateProductRequest,
    PaginationParams,
    SearchParams,
    GetProductsParams,
    PaginatedResponse,
    PriceTier,
} from "../types";

export class ProductService {
    // Categories
    async getCategories(params?: SearchParams): Promise<Category[]> {
        const queryParams = new URLSearchParams();
        if (params?.skip) queryParams.append("skip", params.skip.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.active_only !== undefined)
            queryParams.append("active_only", params.active_only.toString());
        if (params?.search) queryParams.append("search", params.search);

        const url = `${
            API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES
        }?${queryParams.toString()}`;
        return apiService.get<Category[]>(url);
    }

    async getRootCategories(): Promise<Category[]> {
        return apiService.get<Category[]>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES}/root`,
        );
    }

    async getCategoryById(id: string): Promise<Category> {
        return apiService.get<Category>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES}/${id}`,
        );
    }

    async createCategory(
        data: Omit<Category, "category_id" | "create_time" | "update_time">,
    ): Promise<Category> {
        return apiService.post<Category>(
            API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES,
            data,
        );
    }

    async updateCategory(
        id: string,
        data: Partial<Category>,
    ): Promise<Category> {
        return apiService.put<Category>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES}/${id}`,
            data,
        );
    }

    async deleteCategory(id: string, force: boolean = false): Promise<void> {
        const url = force
            ? `${API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES}/${id}?force=true`
            : `${API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES}/${id}`;
        return apiService.delete<void>(url);
    }

    async uploadCategoryImage(
        categoryId: string,
        file: File,
    ): Promise<Category> {
        const formData = new FormData();
        formData.append("file", file);
        return apiService.post<Category>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES}/${categoryId}/image`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } },
        );
    }

    async deleteCategoryImage(categoryId: string): Promise<Category> {
        return apiService.delete<Category>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES}/${categoryId}/image`,
        );
    }

    async getCategoryTree(
        activeOnly: boolean = true,
    ): Promise<CategoryWithChildren[]> {
        return apiService.get<CategoryWithChildren[]>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES}/tree?active_only=${activeOnly}`,
        );
    }

    async getCategoryBySlug(slug: string): Promise<Category> {
        return apiService.get<Category>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES}/slug/${slug}`,
        );
    }

    async getCategoryBreadcrumbs(id: string): Promise<CategoryBreadcrumb[]> {
        return apiService.get<CategoryBreadcrumb[]>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES}/${id}/breadcrumbs`,
        );
    }

    async setProductCategories(
        productId: string,
        categoryIds: string[],
    ): Promise<Product> {
        return apiService.put<Product>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BASE}/${productId}/categories`,
            categoryIds,
        );
    }

    // Brands
    async getBrands(params?: SearchParams): Promise<Brand[]> {
        const queryParams = new URLSearchParams();
        if (params?.skip) queryParams.append("skip", params.skip.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.active_only !== undefined)
            queryParams.append("active_only", params.active_only.toString());
        if (params?.search) queryParams.append("search", params.search);

        const url = `${
            API_CONFIG.ENDPOINTS.PRODUCTS.BRANDS
        }?${queryParams.toString()}`;
        return apiService.get<Brand[]>(url);
    }

    async getBrandById(id: string): Promise<Brand> {
        return apiService.get<Brand>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BRANDS}/${id}`,
        );
    }

    async createBrand(
        data: Omit<
            Brand,
            "brand_id" | "create_time" | "update_time" | "slug" | "cover_image_url"
        > & { slug?: string },
    ): Promise<Brand> {
        return apiService.post<Brand>(
            API_CONFIG.ENDPOINTS.PRODUCTS.BRANDS,
            data,
        );
    }

    async updateBrand(id: string, data: Partial<Brand>): Promise<Brand> {
        return apiService.put<Brand>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BRANDS}/${id}`,
            data,
        );
    }

    async deleteBrand(id: string): Promise<void> {
        return apiService.delete<void>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BRANDS}/${id}`,
        );
    }

    async uploadBrandImage(brandId: string, file: File): Promise<Brand> {
        const formData = new FormData();
        formData.append("file", file);
        return apiService.post<Brand>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BRANDS}/${brandId}/image`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } },
        );
    }

    async deleteBrandImage(brandId: string): Promise<Brand> {
        return apiService.delete<Brand>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BRANDS}/${brandId}/image`,
        );
    }

    /** The wide banner on the brand's shop page. */
    async uploadBrandCover(brandId: string, file: File): Promise<Brand> {
        const formData = new FormData();
        formData.append("file", file);
        return apiService.post<Brand>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BRANDS}/${brandId}/cover`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } },
        );
    }

    async deleteBrandCover(brandId: string): Promise<Brand> {
        return apiService.delete<Brand>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BRANDS}/${brandId}/cover`,
        );
    }

    // Products
    async getProducts(
        params: GetProductsParams = {},
    ): Promise<PaginatedResponse<Product>> {
        const {
            skip = 0,
            limit = 100,
            active_only = true,
            featured_only = false,
            category_id,
            brand_id,
            search,
            include_stock = true,
            is_active,
            is_visible,
            is_featured,
            min_price,
            max_price,
            min_cost_price,
            max_cost_price,
            on_sale_only,
            stock_status,
            created_from,
            created_to,
            sort_by,
            lean,
        } = params;

        const queryParams = new URLSearchParams({
            skip: skip.toString(),
            limit: limit.toString(),
            active_only: active_only.toString(),
            featured_only: featured_only.toString(),
            include_stock: include_stock.toString(),
            ...(category_id && { category_id }),
            ...(brand_id && { brand_id }),
            ...(search && { search }),
            ...(is_active !== undefined && { is_active: is_active.toString() }),
            ...(is_visible !== undefined && { is_visible: is_visible.toString() }),
            ...(is_featured !== undefined && { is_featured: is_featured.toString() }),
            ...(min_price !== undefined && { min_price: min_price.toString() }),
            ...(max_price !== undefined && { max_price: max_price.toString() }),
            ...(min_cost_price !== undefined && {
                min_cost_price: min_cost_price.toString(),
            }),
            ...(max_cost_price !== undefined && {
                max_cost_price: max_cost_price.toString(),
            }),
            ...(on_sale_only !== undefined && { on_sale_only: on_sale_only.toString() }),
            ...(stock_status && { stock_status }),
            ...(created_from && { created_from }),
            ...(created_to && { created_to }),
            ...(sort_by && { sort_by }),
            ...(lean && { lean: "true" }),
        });

        const url = `${
            API_CONFIG.ENDPOINTS.PRODUCTS.BASE
        }/paginated?${queryParams.toString()}`;

        // Use the new paginated endpoint that returns metadata
        return apiService.get<PaginatedResponse<Product>>(url);
    }

    async getProductById(id: string): Promise<Product> {
        return apiService.get<Product>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BASE}/${id}`,
        );
    }

    // Exact-match lookup, distinct from getProducts({ search }) which does
    // a tokenized substring search. Rejects (404, via apiService) when no
    // product has this exact SKU — callers that want a soft "not found"
    // should catch that themselves.
    async getProductBySku(sku: string): Promise<Product> {
        return apiService.get<Product>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BASE}/sku/${encodeURIComponent(sku)}`,
        );
    }

    async createProduct(data: CreateProductRequest): Promise<Product> {
        return apiService.post<Product>(
            API_CONFIG.ENDPOINTS.PRODUCTS.BASE,
            data,
        );
    }

    async updateProduct(
        id: string,
        data: Partial<CreateProductRequest>,
    ): Promise<Product> {
        return apiService.put<Product>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BASE}/${id}`,
            data,
        );
    }

    /** Whether a web address (/products/<slug>) is free for this product. */
    async checkProductSlug(
        slug: string,
        excludeId?: string,
    ): Promise<{ available: boolean; reason: string | null }> {
        const params = new URLSearchParams({ slug, ...(excludeId ? { exclude_id: excludeId } : {}) });
        return apiService.get(`${API_CONFIG.ENDPOINTS.PRODUCTS.BASE}/slugs/check?${params.toString()}`);
    }

    /** Replaces the product's combo prices; an empty list removes them. */
    async updatePriceTiers(id: string, tiers: PriceTier[]): Promise<Product> {
        return apiService.put<Product>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BASE}/${id}/price-tiers`,
            { tiers },
        );
    }

    async deleteProduct(id: string): Promise<void> {
        return apiService.delete<void>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.BASE}/${id}`,
        );
    }

    async searchProducts(params: {
        q: string;
        category_id?: string;
        brand_id?: string;
        min_price?: number;
        max_price?: number;
    }): Promise<Product[]> {
        const queryParams = new URLSearchParams();
        queryParams.append("q", params.q);
        if (params.category_id)
            queryParams.append("category_id", params.category_id);
        if (params.brand_id) queryParams.append("brand_id", params.brand_id);
        if (params.min_price)
            queryParams.append("min_price", params.min_price.toString());
        if (params.max_price)
            queryParams.append("max_price", params.max_price.toString());

        const url = `${
            API_CONFIG.ENDPOINTS.PRODUCTS.SEARCH
        }?${queryParams.toString()}`;
        return apiService.get<Product[]>(url);
    }

    // Stock Management
    async getStock(
        params?: PaginationParams & {
            product_id?: string;
            low_stock_only?: boolean;
        },
    ): Promise<Stock[]> {
        const queryParams = new URLSearchParams();
        if (params?.skip) queryParams.append("skip", params.skip.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.product_id)
            queryParams.append("product_id", params.product_id);
        if (params?.low_stock_only !== undefined)
            queryParams.append(
                "low_stock_only",
                params.low_stock_only.toString(),
            );

        const url = `${
            API_CONFIG.ENDPOINTS.PRODUCTS.STOCK
        }?${queryParams.toString()}`;
        return apiService.get<Stock[]>(url);
    }

    async getStockByProduct(productId: string): Promise<Stock> {
        return apiService.get<Stock>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.STOCK}/product/${productId}`,
        );
    }

    async createStock(
        data: Omit<
            Stock,
            "stock_id" | "is_low_stock" | "create_time" | "update_time"
        >,
    ): Promise<Stock> {
        return apiService.post<Stock>(
            API_CONFIG.ENDPOINTS.PRODUCTS.STOCK,
            data,
        );
    }

    async updateStock(id: string, data: Partial<Stock>): Promise<Stock> {
        return apiService.put<Stock>(
            `${API_CONFIG.ENDPOINTS.PRODUCTS.STOCK}/${id}`,
            data,
        );
    }

    async createStockMovement(
        data: Omit<
            StockMovement,
            "movement_id" | "create_time" | "update_time"
        >,
    ): Promise<StockMovement> {
        return apiService.post<StockMovement>(
            API_CONFIG.ENDPOINTS.PRODUCTS.STOCK_MOVEMENTS,
            data,
        );
    }

    async getStockMovements(
        params?: PaginationParams & {
            stock_id?: string;
            reason?: StockMovement["reason"];
        },
    ): Promise<StockMovement[]> {
        const queryParams = new URLSearchParams();
        if (params?.skip) queryParams.append("skip", params.skip.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.stock_id) queryParams.append("stock_id", params.stock_id);
        if (params?.reason) queryParams.append("reason", params.reason);

        const url = `${
            API_CONFIG.ENDPOINTS.PRODUCTS.STOCK_MOVEMENTS
        }?${queryParams.toString()}`;
        return apiService.get<StockMovement[]>(url);
    }

    // Export Methods
    async exportCategories(params?: {
        search?: string;
        active_only?: boolean;
        sort_by?: string;
        sort_order?: string;
    }): Promise<Blob> {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append("search", params.search);
        if (params?.active_only !== undefined)
            queryParams.append("active_only", params.active_only.toString());
        if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
        if (params?.sort_order)
            queryParams.append("sort_order", params.sort_order);

        const url = `${
            API_CONFIG.ENDPOINTS.PRODUCTS.CATEGORIES
        }/export/csv?${queryParams.toString()}`;
        return apiService.get<Blob>(url, { responseType: "blob" });
    }

    async exportBrands(params?: {
        search?: string;
        active_only?: boolean;
        sort_by?: string;
        sort_order?: string;
    }): Promise<Blob> {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append("search", params.search);
        if (params?.active_only !== undefined)
            queryParams.append("active_only", params.active_only.toString());
        if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
        if (params?.sort_order)
            queryParams.append("sort_order", params.sort_order);

        const url = `${
            API_CONFIG.ENDPOINTS.PRODUCTS.BRANDS
        }/export/csv?${queryParams.toString()}`;
        return apiService.get<Blob>(url, { responseType: "blob" });
    }

    // Filter fields are shared with GetProductsParams (via Pick) so export
    // can never drift from what the list/advanced-search actually applied
    // — sort_by/sort_order stay separate params here since this endpoint's
    // sort scheme (a plain column + direction) differs from getProducts'
    // combined "price-asc"-style values, and export's own row order is a
    // lower-stakes concern than the live table's.
    async exportProducts(
        params?: Pick<
            GetProductsParams,
            | "active_only"
            | "featured_only"
            | "category_id"
            | "brand_id"
            | "search"
            | "is_active"
            | "is_visible"
            | "is_featured"
            | "min_price"
            | "max_price"
            | "min_cost_price"
            | "max_cost_price"
            | "stock_status"
            | "created_from"
            | "created_to"
        > & { sort_by?: string; sort_order?: string },
    ): Promise<Blob> {
        const queryParams = new URLSearchParams();
        if (params?.active_only !== undefined)
            queryParams.append("active_only", params.active_only.toString());
        if (params?.featured_only !== undefined)
            queryParams.append(
                "featured_only",
                params.featured_only.toString(),
            );
        if (params?.category_id)
            queryParams.append("category_id", params.category_id);
        if (params?.brand_id) queryParams.append("brand_id", params.brand_id);
        if (params?.search) queryParams.append("search", params.search);
        if (params?.is_active !== undefined)
            queryParams.append("is_active", params.is_active.toString());
        if (params?.is_visible !== undefined)
            queryParams.append("is_visible", params.is_visible.toString());
        if (params?.is_featured !== undefined)
            queryParams.append("is_featured", params.is_featured.toString());
        if (params?.min_price !== undefined)
            queryParams.append("min_price", params.min_price.toString());
        if (params?.max_price !== undefined)
            queryParams.append("max_price", params.max_price.toString());
        if (params?.min_cost_price !== undefined)
            queryParams.append(
                "min_cost_price",
                params.min_cost_price.toString(),
            );
        if (params?.max_cost_price !== undefined)
            queryParams.append(
                "max_cost_price",
                params.max_cost_price.toString(),
            );
        if (params?.stock_status)
            queryParams.append("stock_status", params.stock_status);
        if (params?.created_from)
            queryParams.append("created_from", params.created_from);
        if (params?.created_to)
            queryParams.append("created_to", params.created_to);
        if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
        if (params?.sort_order)
            queryParams.append("sort_order", params.sort_order);

        const url = `${
            API_CONFIG.ENDPOINTS.PRODUCTS.BASE
        }/export/csv?${queryParams.toString()}`;
        return apiService.get<Blob>(url, { responseType: "blob" });
    }
}

export const productService = new ProductService();
